import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import MarketStateService from '@app/model/market_state/service/MarketStateService';
import { MarketStateGraphQLQueryKeys } from '@app/model/market_state/service/MarketStateGraphQLQueryKeys';
import Month from '@app/model/market_state/Month';
import MarketStateFilterInput from '@app/model/market_state/input/MarketStateFilterInput';
import MarketStateSnapshotOutput from '@app/model/market_state/output/MarketStateSnapshotOutput';
import { MarketStateStatDateSnapshot } from '@app/model/market_state/MarketStateStatDateSnapshot';

export function useMarketStateForPeriodQuery(
	companyHotelId: number,
	year: number,
	month: Month,
	startStatDate: string,
	endStatDate: string,
	filterInput: MarketStateFilterInput,
	options?: Omit<UseQueryOptions<MarketStateSnapshotOutput, unknown, MarketStateSnapshotOutput | undefined>, 'queryKey' | 'queryFn'>,
) {
	return useQuery({
		queryKey: MarketStateGraphQLQueryKeys.details(companyHotelId, year, month, filterInput),
		refetchOnWindowFocus: false,
		queryFn: () => MarketStateService.findMarketStateForPeriod({
			companyHotelId,
			year,
			month,
			startStatDate,
			endStatDate,
			filterInput,
		}),
		...options,
		select: (data: MarketStateSnapshotOutput | Record<string, any>) => {
			if (!data) {
				return undefined;
			}
			return data instanceof MarketStateSnapshotOutput ? data : new MarketStateSnapshotOutput(data as MarketStateSnapshotOutput);
		},
	});
}

export function useFindPreviousMarketStateForDayQuery(
	companyHotelId: number,
	statDate: string,
	filterInput: MarketStateFilterInput,
	fromDate: string,
	options?: Omit<UseQueryOptions<MarketStateStatDateSnapshot | null, unknown, MarketStateStatDateSnapshot | undefined>, 'queryKey' | 'queryFn'>,
) {
	return useQuery({
		refetchOnWindowFocus: false,
		queryKey: MarketStateGraphQLQueryKeys.previous(companyHotelId, statDate, filterInput),
		queryFn: async () => {
			const previousSnapshot = await MarketStateService.findPreviousMarketStateForDay({
				companyHotelId,
				statDate,
				filterInput,
				fromDate,
			});
			return previousSnapshot ?? null;
		},
		...options,
		select: (data: MarketStateStatDateSnapshot | Record<string, any> | null) => {
			if (!data) {
				return undefined;
			}
			return data instanceof MarketStateStatDateSnapshot ? data : new MarketStateStatDateSnapshot(data as MarketStateStatDateSnapshot);
		},
	});
}


export function useMarketStateDayPriceEvolutionForHistoryQuery(
	companyHotelId: number,
	statDate: string,
	filterInput: MarketStateFilterInput,
	options?: Omit<UseQueryOptions<MarketStateStatDateSnapshot[], unknown, MarketStateStatDateSnapshot[]>, 'queryKey' | 'queryFn'>,
) {
	return useQuery({
		refetchOnWindowFocus: false,
		staleTime: 5 * 60 * 1000, // 5 minutes
		queryKey: MarketStateGraphQLQueryKeys.priceEvolutionHistory(companyHotelId, statDate, filterInput),
		queryFn: () => MarketStateService.findMarketStateDayPriceEvolutionForHistory({
			companyHotelId,
			statDate,
			filterInput,
		}),
		...options,
		select: (data: (MarketStateStatDateSnapshot | Record<string, any>)[]) => {
			if (!data) {
				return [];
			}
			return data.map(item => item instanceof MarketStateStatDateSnapshot ? item : new MarketStateStatDateSnapshot(item as MarketStateStatDateSnapshot));
		},
	});
}

export function useMarketStateDayPriceEvolutionForTimeframeQuery(
	companyHotelId: number,
	statDate: string,
	filterInput: MarketStateFilterInput,
	options?: Omit<UseQueryOptions<MarketStateStatDateSnapshot[], unknown, MarketStateStatDateSnapshot[]>, 'queryKey' | 'queryFn'>,
) {
	const queryKey = MarketStateGraphQLQueryKeys.priceEvolutionTimeframe(companyHotelId, statDate, filterInput);

	return useQuery({
		refetchOnWindowFocus: false,
		staleTime: 5 * 60 * 1000, // 5 minutes
		queryKey: queryKey,
		queryFn: () => MarketStateService.findMarketStateDayPriceEvolutionForTimeframe({
			companyHotelId,
			statDate,
			filterInput,
		}),
		...options,
		select: (data: (MarketStateStatDateSnapshot | Record<string, any>)[]) => {
			if (!data) {
				return [];
			}
			return data.map(item => item instanceof MarketStateStatDateSnapshot ? item : new MarketStateStatDateSnapshot(item as MarketStateStatDateSnapshot));
		},
	});
}
